import { useState } from 'react';
import { Bell, Calendar, Clock, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button, ConfirmDialog } from '@travellegacy/nexops-design-system';
import { useCancelReminder, useDeleteReminder } from '../hooks/useReminders';
import type { Reminder } from '../services/reminders.service';

interface ReminderCardProps {
  reminder: Reminder;
}

const STATUS_CONFIG: Record<
  Reminder['status'],
  { label: string; variant: 'default' | 'success' | 'warning' | 'error' }
> = {
  PENDING: {
    label: 'Pendente',
    variant: 'warning',
  },
  SENT: {
    label: 'Enviado',
    variant: 'success',
  },
  FAILED: {
    label: 'Falhou',
    variant: 'error',
  },
  CANCELLED: {
    label: 'Cancelado',
    variant: 'default',
  },
};

const NOTIFY_BEFORE_LABELS: Record<number, string> = {
  5: '5 min',
  15: '15 min',
  30: '30 min',
  60: '1 hora',
  120: '2 horas',
};

export function ReminderCard({ reminder }: ReminderCardProps) {
  const cancelReminder = useCancelReminder();
  const deleteReminder = useDeleteReminder();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const statusConfig = STATUS_CONFIG[reminder.status];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR });
  };

  const handleConfirmCancel = () => {
    cancelReminder.mutate(reminder.id);
  };

  const handleConfirmDelete = () => {
    deleteReminder.mutate(reminder.id);
  };

  const canCancel = reminder.status === 'PENDING';
  const canDelete = reminder.status !== 'PENDING';

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Bell className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg truncate">{reminder.title}</CardTitle>
              {reminder.description && (
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {reminder.description}
                </p>
              )}
            </div>
          </div>

          <Badge variant={statusConfig.variant}>
            {statusConfig.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Info */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(reminder.reminderDateTime)}</span>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>Notificar {NOTIFY_BEFORE_LABELS[reminder.notifyBefore]} antes</span>
          </div>

          {reminder.sentAt && (
            <div className="text-xs text-muted-foreground mt-2">
              Enviado em{' '}
              {format(new Date(reminder.sentAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </div>
          )}
        </div>

        {/* Actions */}
        {(canCancel || canDelete) && (
          <div className="flex gap-2 pt-2 border-t">
            {canCancel && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancelDialog(true)}
                disabled={cancelReminder.isPending}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-1" />
                Cancelar
              </Button>
            )}

            {canDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleteReminder.isPending}
                className="flex-1"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Deletar
              </Button>
            )}
          </div>
        )}
      </CardContent>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        title="Cancelar Lembrete"
        description={
          <>
            Tem certeza que deseja cancelar o lembrete <strong>"{reminder.title}"</strong>?
            <br />
            <br />
            Após cancelado, você não receberá mais notificações deste lembrete.
          </>
        }
        confirmText="Sim, cancelar"
        cancelText="Não, manter"
        onConfirm={handleConfirmCancel}
        variant="warning"
        icon="warning"
        isLoading={cancelReminder.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Deletar Lembrete"
        description={
          <>
            Tem certeza que deseja deletar permanentemente o lembrete <strong>"{reminder.title}"</strong>?
            <br />
            <br />
            Esta ação não pode ser desfeita.
          </>
        }
        confirmText="Sim, deletar"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="warning"
        icon="warning"
        isLoading={deleteReminder.isPending}
      />
    </Card>
  );
}
